// assets/free-shipping-banner.js
import { CartAddEvent } from '@theme/events';

class FreeShippingBanner extends HTMLElement {
  connectedCallback() {
    // Threshold comes from data-threshold (in dollars)
    this.threshold = parseFloat(this.dataset.threshold);
    if (!isFinite(this.threshold) || this.threshold <= 0) {
      this.hidden = true;
      return;
    }
    this.thresholdCents = Math.round(this.threshold * 100);

    // Update on load/open
    this.#updateFromAjax();

    // Update when an item is added (theme's standard event)
    document.addEventListener(CartAddEvent?.eventName || 'cart:added', this.#updateFromAjax, { passive: true });

    // Update when line items / drawer markup changes. Do not observe this element's own
    // subtree: the banner lives inside <cart-items-component>, and #setMessage uses
    // innerHTML which would retrigger the observer and spam /cart.js indefinitely.
    const drawer = this.closest('cart-drawer-component') || document;
    const items = drawer.querySelector('cart-items-component');
    if (items) {
      this._cartItemsRoot = items;
      this.#attachCartMutationObserver();
    }

    // As an extra safety net, react to clicks on +/- or remove buttons inside the drawer
    drawer.addEventListener('click', (e) => {
      if (e.target.closest('button[name="plus"], button[name="minus"], [data-index], .cart-remove-button')) {
        setTimeout(() => this.#updateFromAjax(), 120);
      }
    });
  }

  disconnectedCallback() {
    this._mo?.disconnect();
    this._cartItemsRoot = undefined;
  }

  #attachCartMutationObserver() {
    const root = this._cartItemsRoot;
    if (!root || !this.isConnected) return;
    this._mo?.disconnect();
    this._mo = new MutationObserver(() => this.#updateFromAjax());
    this._mo.observe(root, { childList: true, subtree: true });
  }

  #setMessage(totalCents) {
    if (totalCents >= this.thresholdCents) {
      this.innerHTML = `<strong>Congratulations!</strong> You've earned Free Shipping!`;
      this.classList.add('is-earned');
      return;
    }
    const remaining = ((this.thresholdCents - totalCents) / 100).toFixed(2);
    this.innerHTML = `You are <span class="remaining">${remaining}</span> away from FREE SHIPPING!`;
    this.classList.remove('is-earned');
  }

  #updateFromAjax = async () => {
    this._mo?.disconnect();
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      const cart = await res.json();
      // Observer is attached in connectedCallback before this await resolves; disconnect
      // again so our own innerHTML does not enqueue another update.
      this._mo?.disconnect();
      this.#setMessage(cart.total_price);
    } catch (e) {
      // Silently ignore; banner will show SSR content
    } finally {
      this.#attachCartMutationObserver();
    }
  };
}

if (!customElements.get('free-shipping-banner')) {
  customElements.define('free-shipping-banner', FreeShippingBanner);
}
